import isApiError from "@insite/client-framework/Common/isApiError";
import { ApiParameter, del, get, post, request, ServiceResult } from "@insite/client-framework/Services/ApiService";

export interface GiftCardModel extends ApiParameter {
    success: boolean;
    data: GiftCardDataModel | null;
    msg: string | null;
}

export interface GiftCardDataModel extends ApiParameter {
    balance: number | string;
    cardNumber: string;
    type: string;
    balanceOnHold: number | string;
    isActivated: boolean;
    isExpired: boolean;
}

export interface AllGiftCardsModel extends ApiParameter {
    giftCardNumber: string;
    appliedAmount: string;
    pin: string;
    balance: string | number;
}

const giftCardUrl = "/api/v1/giftcard";

export async function getGiftCard(giftCard: AllGiftCardsModel): Promise<ServiceResult<GiftCardModel | null>> {
    try {
        const data = await request<GiftCardModel | null>(
            `${giftCardUrl}/validate/?giftCardNumber=${giftCard.giftCardNumber}&pin=${giftCard.pin}`,
            "GET",
            { "Cache-Control": "no-cache" },
        );
        return {
            successful: true,
            result: data,
        };
    } catch (error) {
        if ("status" in error && (error.status === 400 || error.status === 403 || error.status === 404)) {
            return {
                successful: false,
                errorMessage: error?.errorJson?.message || error?.errorMessage || "error",
                statusCode: error.status,
            };
        }
        throw error;
    }
}

export async function loadAllGiftCards(): Promise<ServiceResult<AllGiftCardsModel[] | null>> {
    try {
        const data = await request<AllGiftCardsModel[] | null>(`${giftCardUrl}`, "GET", {
            "Cache-Control": "no-cache",
        });
        return {
            successful: true,
            result: data,
        };
    } catch (error) {
        if ("status" in error && (error.status === 400 || error.status === 403 || error.status === 404)) {
            return {
                successful: false,
                errorMessage: error?.errorJson?.message || error?.errorMessage || "error",
                statusCode: error.status,
            };
        }
        throw error;
    }
}

export async function applyGiftCard(giftCard: AllGiftCardsModel): Promise<ServiceResult<string>> {
    try {
        const data = await post<AllGiftCardsModel, string>(`${giftCardUrl}`, giftCard);
        return {
            successful: true,
            result: data,
        };
    } catch (error) {
        if ("status" in error && (error.status === 400 || error.status === 403 || error.status === 404)) {
            return {
                successful: false,
                errorMessage: error?.errorJson?.message || error?.errorMessage || "error",
                statusCode: error.status,
            };
        }
        throw error;
    }
}

export async function removeGiftCard(giftCardNumber: string): Promise<ServiceResult<string>> {
    try {
        await del(`${giftCardUrl}?giftCardNumber=${giftCardNumber}`);
        return {
            successful: true,
            result: "Removed Gift Card Successfully!",
        };
    } catch (error) {
        if ("status" in error && (error.status === 400 || error.status === 403 || error.status === 404)) {
            return {
                successful: false,
                errorMessage: error?.errorJson?.message || error?.errorMessage || "error",
                statusCode: error.status,
            };
        }
        throw error;
    }
}
